import { PropsWithChildren, useMemo, useState } from 'react';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import {
  EventWithData,
  ListingDetails,
  SaleLineItemCoverage,
} from 'src/WebApiController';
import { BuyerWithSalesDto } from 'src/WebApiController';
import { createContext, useContext } from 'use-context-selector';

import {
  AdyenSession,
  useRetailSaleOperations,
} from './RetailSaleLocalContext.hooks';

export interface IRetailSaleContext {
  adyenSession: AdyenSession | null;
  selectedBuyer: BuyerWithSalesDto | undefined | null;
  disabled: boolean;
  onUpsertBuyer: () => Promise<boolean>;
  onPayAndComplete: () => void;
  onCompleteAsUnpaid: () => void;
  /** Submits the sale with external payment fields — skips the Adyen drop-in. */
  onCompleteExternalPayment: () => void;
  onAdyenPaymentCompleted: (result: AdyenPaymentCompletedResult) => void;
  onAdyenError: (error: { message: string }) => void;
  selectedStoredPaymentMethodId: string | null;
  setSelectedStoredPaymentMethodId: (id: string | null) => void;
  setSelectedBuyer: (buyer?: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  setIsPartialPayment: (value: boolean) => void;
  partialAmount: number;
  setPartialAmount: (amount: number) => void;
  coverageDialogSaleId: number | null;
  coverageDialogSalesGroupId: string | null;
  onCoverageConfirmed: (coverages: SaleLineItemCoverage[]) => void;
  onCoverageDialogCancel: () => void;
}

export const RetailSaleLocalContext = createContext<IRetailSaleContext | null>(
  null
);

type RetailSaleLocalContextProviderProps = PropsWithChildren<{
  event?: EventWithData;
  listing?: ListingDetails;
}>;

export const RetailSaleLocalContextProvider = ({
  children,
  listing,
  event,
}: RetailSaleLocalContextProviderProps) => {
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerWithSalesDto>();

  const [selectedStoredPaymentMethodId, setSelectedStoredPaymentMethodIdState] =
    useState<string | null>(null);

  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState(0);

  const {
    disabled,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onCompleteExternalPayment,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
    coverageDialogSaleId,
    coverageDialogSalesGroupId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  } = useRetailSaleOperations({
    event,
    listing,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    setSelectedBuyer,
    isPartialPayment,
    partialAmount,
  });

  const value = useMemo<IRetailSaleContext>(
    () => ({
      adyenSession,
      selectedBuyer,
      disabled,
      onUpsertBuyer,
      onPayAndComplete,
      onCompleteAsUnpaid,
      onCompleteExternalPayment,
      onAdyenPaymentCompleted,
      onAdyenError,
      selectedStoredPaymentMethodId,
      setSelectedStoredPaymentMethodId: setSelectedStoredPaymentMethodIdState,
      setSelectedBuyer,
      isPartialPayment,
      setIsPartialPayment,
      partialAmount,
      setPartialAmount,
      coverageDialogSaleId,
      coverageDialogSalesGroupId,
      onCoverageConfirmed,
      onCoverageDialogCancel,
    }),
    [
      adyenSession,
      selectedBuyer,
      disabled,
      onUpsertBuyer,
      onPayAndComplete,
      onCompleteAsUnpaid,
      onCompleteExternalPayment,
      onAdyenPaymentCompleted,
      onAdyenError,
      selectedStoredPaymentMethodId,
      isPartialPayment,
      partialAmount,
      coverageDialogSaleId,
      coverageDialogSalesGroupId,
      onCoverageConfirmed,
      onCoverageDialogCancel,
    ]
  );

  return (
    <RetailSaleLocalContext.Provider value={value}>
      {children}
    </RetailSaleLocalContext.Provider>
  );
};

export function useRetailSaleContext(): IRetailSaleContext {
  const ctx = useContext(RetailSaleLocalContext);
  if (!ctx) {
    throw new Error(
      'useRetailSaleContext must be used within RetailSaleLocalContextProvider'
    );
  }
  return ctx;
}
